import { useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from '@/lib/useInView'

/**
 * Мини-график роста метрики по точкам.
 * Тонкая сглаженная линия + видимые data-point точки + мягкая заливка
 * под графиком. Сглаживание — Catmull-Rom через cubic Bézier (tension 0.5),
 * чтобы кривые с разным числом точек и разной формой действительно
 * выглядели по-разному, а не одинаковой J-кривой.
 *
 * При появлении в viewport линия рисуется анимацией (stroke-dashoffset),
 * затем проявляется заливка и опорные точки с лёгким стэггером.
 *
 * @param points   Массив чисел — нормализуется внутри, чтобы занимать
 *                 всю высоту области (за вычетом padding'а).
 * @param label    Короткая подпись (a11y).
 * @param width    Ширина SVG в px (по умолчанию 160).
 * @param height   Высота SVG в px (по умолчанию 48).
 */
export function Sparkline({
  points,
  label,
  className,
  width = 160,
  height = 48,
}: {
  points: number[]
  label?: string
  className?: string
  width?: number
  height?: number
}) {
  const ref = useRef<SVGSVGElement>(null)
  const inView = useInView(ref, { once: true, margin: '-10%' })
  const [pathLen, setPathLen] = useState<number>(0)
  const pathRef = useRef<SVGPathElement>(null)

  // Padding внутри SVG — чтобы точки + штрихи не резались на границах.
  const padX = 4
  const padY = 6
  const w = width - padX * 2
  const h = height - padY * 2

  const { coords, linePath, fillPath, lastPoint } = useMemo(() => {
    const min = Math.min(...points)
    const max = Math.max(...points)
    const range = max - min || 1

    // X равномерно распределён, Y инвертирован (SVG y растёт вниз)
    const cs = points.map((v, i) => {
      const x = padX + (i / (points.length - 1)) * w
      const y = padY + (1 - (v - min) / range) * h
      return { x, y }
    })

    // Catmull-Rom → cubic Bézier. Для краёв клонируем граничные точки
    // (как при «open» сплайне), tension = 0.5 даёт мягкую естественную форму.
    const segments: string[] = [`M${cs[0].x.toFixed(2)},${cs[0].y.toFixed(2)}`]
    for (let i = 0; i < cs.length - 1; i++) {
      const p0 = cs[i - 1] ?? cs[i]
      const p1 = cs[i]
      const p2 = cs[i + 1]
      const p3 = cs[i + 2] ?? p2
      const c1x = p1.x + (p2.x - p0.x) / 6
      const c1y = p1.y + (p2.y - p0.y) / 6
      const c2x = p2.x - (p3.x - p1.x) / 6
      const c2y = p2.y - (p3.y - p1.y) / 6
      segments.push(
        `C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`,
      )
    }
    const line = segments.join(' ')

    const last = cs[cs.length - 1]
    const fill = `${line} L${last.x.toFixed(2)},${height - padY + 4} L${cs[0].x.toFixed(2)},${height - padY + 4} Z`

    return { coords: cs, linePath: line, fillPath: fill, lastPoint: last }
  }, [points, w, h, padX, padY, height])

  useEffect(() => {
    if (pathRef.current) {
      setPathLen(pathRef.current.getTotalLength())
    }
  }, [linePath])

  // Уникальный id градиента, иначе при нескольких спарклайнах с одинаковой
  // последовательностью значений будет коллизия. Берём длину + первое/последнее.
  const gradId = `sparkline-grad-${points.length}-${points[0]}-${points[points.length - 1]}`

  return (
    <svg
      ref={ref}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={className}
      role={label ? 'img' : 'presentation'}
      aria-label={label}
      style={{ overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Тонкий baseline — ставим точки полупрозрачными, чтобы было видно,
          сколько в графике опорных точек (4, 5 или 6 — глазом отличимо). */}
      {coords.map((c, i) => (
        <circle
          key={`tick-${i}`}
          cx={c.x}
          cy={c.y}
          r="1.6"
          fill="currentColor"
          opacity={inView ? 0.55 : 0}
          style={{
            transition: `opacity 220ms ease ${600 + i * 80}ms`,
          }}
        />
      ))}

      {/* Заливка — проявляется плавно после отрисовки линии */}
      <path
        d={fillPath}
        fill={`url(#${gradId})`}
        style={{
          opacity: inView ? 1 : 0,
          transition: 'opacity 500ms ease 800ms',
        }}
      />

      {/* Основная линия — рисуется от начала к концу */}
      <path
        ref={pathRef}
        d={linePath}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={
          pathLen
            ? {
                strokeDasharray: pathLen,
                strokeDashoffset: inView ? 0 : pathLen,
                transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1) 200ms',
              }
            : undefined
        }
      />

      {/* Акцентная точка в конце (финальное значение) — с белым колечком,
          чтобы заметнее «припарковалась» поверх линии. */}
      <circle
        cx={lastPoint.x}
        cy={lastPoint.y}
        r="3.2"
        fill="currentColor"
        style={{
          opacity: inView ? 1 : 0,
          transition: 'opacity 300ms ease 1300ms',
        }}
      />
    </svg>
  )
}
